import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useSyncExternalStore,
  ReactNode,
} from 'react';
import { UserProfile, UserRoles } from '@amzn/awscat-pattern-library-service-model';

import type { LoadedDocumentKind } from '../model/LoadedDocument';
import { ActiveRole, UserService } from '../services/UserService';
import { UserServiceContext, useUserService } from './UserServiceContext';

export type { ActiveRole } from '../services/UserService';

export interface CurrentUser {
  readonly userId: string;
  readonly name: string;
  readonly email: string;
  readonly roles: ReadonlySet<UserRoles>;
  readonly activeRole: ActiveRole;
  readonly availableRoles: readonly ActiveRole[];
  /**
   * Whether the user may perform bar raiser actions on content in the given tech
   * domains: Publisher hat active AND a BarRaiser content role matching the content.
   */
  readonly canActAsBarRaiserForContent: (
    mainTechDomain: string | undefined,
    techDomains: readonly string[] | undefined,
  ) => boolean;
  /**
   * Whether the user may start a new inline comment thread on the given content:
   * a draft, plus the Admin hat or a matching bar raiser role. Reading existing
   * threads is not gated by this.
   */
  readonly canStartCommentThreadForContent: (
    documentKind: LoadedDocumentKind,
    mainTechDomain: string | undefined,
    techDomains: readonly string[] | undefined,
  ) => boolean;
  /**
   * Whether the user may see code repository review ticket links: a privileged
   * role, any bar raiser role, or the content's owner or a co-author.
   */
  readonly canSeeCodeReviewTicketsForContent: (
    ownerId: string | undefined,
    coAuthorIds: readonly string[],
  ) => boolean;
  /**
   * Whether the user may see a published version's activity: Admin, Publisher,
   * Tech writer, any bar raiser role, or the content's owner or a co-author.
   */
  readonly canSeeActivityForContent: (
    ownerId: string | undefined,
    coAuthorIds: readonly string[],
  ) => boolean;
  readonly setActiveRole: (role: ActiveRole) => void;
}

const UserContext = createContext<CurrentUser | undefined>(undefined);

interface UserProviderProps {
  readonly userProfile: UserProfile;
  readonly children: ReactNode;
}

export function UserProvider({ userProfile, children }: UserProviderProps) {
  const service = useUserService();

  // When the profile prop changes, then seed the service during render so this
  // render and its children already read the new profile. Listeners are told
  // after commit: emitting here would update other subscribed components while
  // this one is still rendering.
  service.stageUserProfile(userProfile);
  useLayoutEffect(() => {
    service.notifyStagedUserProfile();
  }, [service, userProfile]);

  // When the service switches roles, then re-render with the new active role.
  const subscribeToRole = useCallback(
    (onStoreChange: () => void) => service.on('roleChanged', onStoreChange),
    [service],
  );
  const activeRole = useSyncExternalStore(subscribeToRole, () => service.activeRole);

  const user = useMemo<CurrentUser>(
    () => ({
      userId: userProfile.userId,
      name: userProfile.name,
      email: userProfile.email,
      roles: service.roles,
      activeRole,
      availableRoles: service.availableRoles,
      canActAsBarRaiserForContent: (mainTechDomain, techDomains) =>
        service.canActAsBarRaiserForContent(mainTechDomain, techDomains),
      canStartCommentThreadForContent: (documentKind, mainTechDomain, techDomains) =>
        service.canStartCommentThreadForContent(documentKind, mainTechDomain, techDomains),
      canSeeCodeReviewTicketsForContent: (ownerId, coAuthorIds) =>
        service.canSeeCodeReviewTicketsForContent(ownerId, coAuthorIds),
      canSeeActivityForContent: (ownerId, coAuthorIds) =>
        service.canSeeActivityForContent(ownerId, coAuthorIds),
      setActiveRole: (role: ActiveRole) => service.setActiveRole(role),
    }),
    [service, userProfile, activeRole],
  );

  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
}

export function useCurrentUser(): CurrentUser {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useCurrentUser must be used within UserProvider');
  }
  return context;
}

export function useOptionalCurrentUser(): CurrentUser | null {
  return useContext(UserContext) ?? null;
}
